// ============================================================================
// BP-04-02: Documents Frontend - FolderTree Component
// ============================================================================

import React, { useState } from 'react';
import {
  Folder,
  FolderOpen,
  ChevronRight,
  ChevronDown,
  Plus,
  MoreHorizontal,
  Pencil,
  Trash2,
  FolderInput,
  Home,
} from 'lucide-react';
import { cn } from '@/core/lib/utils';
import type { DocumentFolder, FolderTreeProps } from '../types/documentsTypes';

// -----------------------------------------------------------------------------
// FolderTreeItem Component
// -----------------------------------------------------------------------------

interface FolderTreeItemProps {
  folder: DocumentFolder;
  level: number;
  isSelected: boolean;
  isExpanded: boolean;
  onSelect: (folderId: string) => void;
  onToggleExpand: (folderId: string) => void;
  onCreateFolder?: (parentId: string) => void;
  onEditFolder?: (folder: DocumentFolder) => void;
  onDeleteFolder?: (folder: DocumentFolder) => void;
  onMoveFolder?: (folder: DocumentFolder) => void;
}

const FolderTreeItem: React.FC<FolderTreeItemProps> = ({
  folder,
  level,
  isSelected,
  isExpanded,
  onSelect,
  onToggleExpand,
  onCreateFolder,
  onEditFolder,
  onDeleteFolder,
  onMoveFolder,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const hasChildren = folder.children && folder.children.length > 0;

  const handleToggleExpand = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (hasChildren) {
      onToggleExpand(folder.id);
    }
  };

  const handleSelect = () => {
    onSelect(folder.id);
  };

  return (
    <div>
      <div
        className={cn(
          'group flex items-center gap-1 py-1.5 px-2 rounded-lg cursor-pointer transition-all duration-150',
          isSelected
            ? 'bg-sage text-white'
            : 'hover:bg-cream-dark text-navy'
        )}
        style={{ paddingLeft: `${level * 12 + 8}px` }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => {
          setIsHovered(false);
          setIsMenuOpen(false);
        }}
        onClick={handleSelect}
      >
        {/* Expand/Collapse button */}
        <button
          onClick={handleToggleExpand}
          className={cn(
            'p-0.5 rounded transition-colors',
            hasChildren ? 'hover:bg-black/10' : 'invisible'
          )}
        >
          {isExpanded ? (
            <ChevronDown className="w-3.5 h-3.5" />
          ) : (
            <ChevronRight className="w-3.5 h-3.5" />
          )}
        </button>

        {/* Folder icon */}
        <div
          className={cn(
            'p-1 rounded transition-colors',
            isSelected ? 'text-white' : folder.color ? '' : 'text-sage'
          )}
          style={!isSelected && folder.color ? { color: folder.color } : undefined}
        >
          {isExpanded ? (
            <FolderOpen className="w-4 h-4" />
          ) : (
            <Folder className="w-4 h-4" />
          )}
        </div>

        {/* Folder name */}
        <span className="flex-1 truncate text-sm font-medium">{folder.name}</span>

        {/* Document count */}
        {folder.document_count > 0 && (
          <span
            className={cn(
              'text-xs px-1.5 py-0.5 rounded-full',
              isSelected
                ? 'bg-white/20 text-white'
                : 'bg-cream text-warm-gray'
            )}
          >
            {folder.document_count}
          </span>
        )}

        {/* Actions menu */}
        {isHovered && (onCreateFolder || onEditFolder || onDeleteFolder) && (
          <div className="relative">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsMenuOpen(!isMenuOpen);
              }}
              className={cn(
                'p-1 rounded transition-colors',
                isSelected ? 'hover:bg-white/20' : 'hover:bg-warm-gray/10'
              )}
            >
              <MoreHorizontal className="w-4 h-4" />
            </button>

            {isMenuOpen && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsMenuOpen(false);
                  }}
                />
                <div className="absolute right-0 top-full mt-1 z-50 min-w-[140px] bg-white rounded-lg shadow-lg border border-warm-gray/10 py-1 text-navy">
                  {onCreateFolder && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsMenuOpen(false);
                        onCreateFolder(folder.id);
                      }}
                      className="w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-cream transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                      New Subfolder
                    </button>
                  )}
                  {onEditFolder && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsMenuOpen(false);
                        onEditFolder(folder);
                      }}
                      className="w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-cream transition-colors"
                    >
                      <Pencil className="w-4 h-4" />
                      Rename
                    </button>
                  )}
                  {onMoveFolder && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsMenuOpen(false);
                        onMoveFolder(folder);
                      }}
                      className="w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-cream transition-colors"
                    >
                      <FolderInput className="w-4 h-4" />
                      Move
                    </button>
                  )}
                  {onDeleteFolder && (
                    <>
                      <div className="my-1 border-t border-warm-gray/10" />
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsMenuOpen(false);
                          onDeleteFolder(folder);
                        }}
                        className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                        Delete
                      </button>
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Children */}
      {isExpanded && hasChildren && (
        <div className="animate-in slide-in-from-top-2 duration-200">
          {folder.children!.map((child) => (
            <FolderTreeItem
              key={child.id}
              folder={child}
              level={level + 1}
              isSelected={false} // Will be passed from parent
              isExpanded={false} // Will be passed from parent
              onSelect={onSelect}
              onToggleExpand={onToggleExpand}
              onCreateFolder={onCreateFolder}
              onEditFolder={onEditFolder}
              onDeleteFolder={onDeleteFolder}
              onMoveFolder={onMoveFolder}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// -----------------------------------------------------------------------------
// FolderTree Component
// -----------------------------------------------------------------------------

export const FolderTree: React.FC<FolderTreeProps> = ({
  folders,
  selectedFolderId,
  expandedFolderIds,
  onSelectFolder,
  onToggleExpand,
  onCreateFolder,
  onEditFolder,
  onDeleteFolder,
}) => {
  const isRootSelected = selectedFolderId === null;

  const renderFolderItem = (folder: DocumentFolder, level: number) => {
    const isSelected = folder.id === selectedFolderId;
    const isExpanded = expandedFolderIds.includes(folder.id);

    return (
      <FolderTreeItem
        key={folder.id}
        folder={folder}
        level={level}
        isSelected={isSelected}
        isExpanded={isExpanded}
        onSelect={onSelectFolder}
        onToggleExpand={onToggleExpand}
        onCreateFolder={onCreateFolder}
        onEditFolder={onEditFolder}
        onDeleteFolder={onDeleteFolder}
      />
    );
  };

  return (
    <div className="space-y-1">
      {/* Root folder (All Documents) */}
      <div
        className={cn(
          'group flex items-center gap-2 py-2 px-3 rounded-lg cursor-pointer transition-all duration-150',
          isRootSelected
            ? 'bg-sage text-white'
            : 'hover:bg-cream-dark text-navy'
        )}
        onClick={() => onSelectFolder(null)}
      >
        <Home className="w-4 h-4" />
        <span className="flex-1 text-sm font-medium">All Documents</span>
        {onCreateFolder && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onCreateFolder(null);
            }}
            className={cn(
              'p-1 rounded opacity-0 group-hover:opacity-100 transition-all',
              isRootSelected ? 'hover:bg-white/20' : 'hover:bg-warm-gray/10'
            )}
          >
            <Plus className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Folder tree */}
      <div className="pt-2 border-t border-warm-gray/10">
        {folders.length === 0 ? (
          <p className="text-sm text-warm-gray text-center py-4">
            No folders yet
          </p>
        ) : (
          folders.map((folder) => renderFolderItem(folder, 0))
        )}
      </div>
    </div>
  );
};

// -----------------------------------------------------------------------------
// FolderTreeSkeleton Component
// -----------------------------------------------------------------------------

export const FolderTreeSkeleton: React.FC = () => (
  <div className="space-y-2 animate-pulse">
    <div className="h-9 bg-cream-dark rounded-lg" />
    <div className="pt-2 border-t border-warm-gray/10 space-y-1">
      {[1, 2, 3].map((i) => (
        <div key={i} className="h-8 bg-cream-dark rounded-lg" style={{ marginLeft: `${(i - 1) * 12}px` }} />
      ))}
    </div>
  </div>
);

export default FolderTree;

